import { formatDateTime, hasValidValue } from '../utils/format'
import type { DeviceStatusResponse } from '../types/api'
import type { MagoMasterResponse } from '../types/master'

interface DeviceDetailsModalProps {
  device: DeviceStatusResponse
  magoDevices: MagoMasterResponse[] | undefined
  onClose: () => void
}

export default function DeviceDetailsModal({
  device: selectedDevice,
  magoDevices,
  onClose,
}: DeviceDetailsModalProps) {
  return (
    <div
      className="modal show d-block"
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-microchip me-2" />
              端末詳細 - {selectedDevice.mago_id}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <div className="row">
              <div className="col-md-6">
                <h6 className="text-muted mb-2">基本情報</h6>

                <div className="mb-2">
                  <label className="form-label fw-bold">端末ID</label>
                  <div className="form-control-plaintext">{selectedDevice.mago_id}</div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">ステータス</label>
                  <div>
                    <span
                      className={`badge ${selectedDevice.is_online ? 'bg-success' : 'bg-danger'}`}
                    >
                      <i
                        className={`fas fa-circle me-1 ${selectedDevice.is_online ? 'blink' : ''}`}
                      />
                      {selectedDevice.is_online ? 'オンライン' : 'オフライン'}
                    </span>
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">最終確認</label>
                  <div className="form-control-plaintext">
                    {formatDateTime(selectedDevice.last_seen)}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">ファームウェアバージョン</label>
                  <div className="form-control-plaintext font-monospace">
                    {selectedDevice.firmware_version || (
                      <span className="text-muted">利用不可</span>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">グループID</label>
                  <div>
                    {hasValidValue(selectedDevice.group_id) ? (
                      <span className="badge bg-secondary">{selectedDevice.group_id}</span>
                    ) : (
                      <span className="text-muted">利用不可</span>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">SIM ID</label>
                  <div>
                    {hasValidValue(selectedDevice.sim_id) ? (
                      <span className="badge bg-info">{selectedDevice.sim_id}</span>
                    ) : (
                      <span className="text-muted">利用不可</span>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">運用先</label>
                  <div>
                    {hasValidValue(selectedDevice.dest_name) ? (
                      <span className="badge bg-primary">{selectedDevice.dest_name}</span>
                    ) : (
                      <span className="text-muted">利用不可</span>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">利用先</label>
                  <div>
                    {(() => {
                      const magoDevice = magoDevices?.find(
                        d => d.mago_id === selectedDevice.mago_id
                      )
                      const subDestName = magoDevice?.sub_dest_name
                      return hasValidValue(subDestName) ? (
                        <span className="badge bg-secondary">{subDestName}</span>
                      ) : (
                        <span className="text-muted">利用不可</span>
                      )
                    })()}
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <h6 className="text-muted mb-2">端末ステータス</h6>

                <div className="mb-2">
                  <label className="form-label fw-bold">動作モード</label>
                  <div>
                    {selectedDevice.operation_mode ? (
                      <span
                        className={`badge ${selectedDevice.operation_mode === 'normal' ? 'bg-info' : 'bg-warning'}`}
                      >
                        {selectedDevice.operation_mode}
                      </span>
                    ) : (
                      <span className="text-muted">利用不可</span>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">電源状態</label>
                  <div>
                    {selectedDevice.power_state ? (
                      <span
                        className={`badge ${selectedDevice.power_state === 'ON' ? 'bg-success' : 'bg-secondary'}`}
                      >
                        <i className="fas fa-power-off me-1" />
                        {selectedDevice.power_state}
                      </span>
                    ) : (
                      <span className="text-muted">利用不可</span>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">助けてボタン</label>
                  <div>
                    {selectedDevice.emergency_count !== null &&
                    selectedDevice.emergency_count !== undefined ? (
                      <span
                        className={`badge ${selectedDevice.emergency_count > 0 ? 'bg-warning text-dark' : 'bg-light text-dark'}`}
                      >
                        {selectedDevice.emergency_count}
                      </span>
                    ) : (
                      <span className="text-muted">利用不可</span>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">センサーカウント</label>
                  <div>
                    {selectedDevice.sensor_count !== null &&
                    selectedDevice.sensor_count !== undefined ? (
                      <span className="badge bg-secondary">{selectedDevice.sensor_count}</span>
                    ) : (
                      <span className="text-muted">利用不可</span>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">センサー異常</label>
                  <div>
                    {selectedDevice.sensor_fault !== null &&
                    selectedDevice.sensor_fault !== undefined ? (
                      selectedDevice.sensor_fault ? (
                        <span className="badge bg-danger">
                          <i className="fas fa-times-circle me-1" />
                          異常検出
                        </span>
                      ) : (
                        <span className="badge bg-success">
                          <i className="fas fa-check-circle me-1" />
                          正常
                        </span>
                      )
                    ) : (
                      <span className="text-muted">利用不可</span>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">SDカードステータス</label>
                  <div>
                    {selectedDevice.sd_card_error !== null &&
                    selectedDevice.sd_card_error !== undefined ? (
                      selectedDevice.sd_card_error ? (
                        <span className="badge bg-danger">
                          <i className="fas fa-times-circle me-1" />
                          エラー
                        </span>
                      ) : (
                        <span className="badge bg-success">
                          <i className="fas fa-check-circle me-1" />
                          正常
                        </span>
                      )
                    ) : (
                      <span className="text-muted">利用不可</span>
                    )}
                  </div>
                </div>

                <hr className="my-3" />
                <h6 className="text-muted mb-2">再生状態</h6>

                <div className="mb-2">
                  <label className="form-label fw-bold">連続再生失敗回数</label>
                  <div>
                    {selectedDevice.consecutive_playback_failures !== null &&
                    selectedDevice.consecutive_playback_failures !== undefined ? (
                      selectedDevice.consecutive_playback_failures > 0 ? (
                        <span
                          className={`badge ${selectedDevice.consecutive_playback_failures >= 3 ? 'bg-danger' : 'bg-warning text-dark'}`}
                        >
                          <i className="fas fa-exclamation-triangle me-1" />
                          {selectedDevice.consecutive_playback_failures}回連続失敗
                        </span>
                      ) : (
                        <span className="badge bg-success">
                          <i className="fas fa-check-circle me-1" />
                          正常
                        </span>
                      )
                    ) : (
                      <span className="text-muted">利用不可</span>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">最終再生失敗時刻</label>
                  <div className="form-control-plaintext">
                    {selectedDevice.last_playback_failure_time ? (
                      <span className="badge bg-light text-dark">
                        {formatDateTime(selectedDevice.last_playback_failure_time)}
                      </span>
                    ) : (
                      <span className="text-muted">なし</span>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold">最終再生成功時刻</label>
                  <div className="form-control-plaintext">
                    {selectedDevice.last_playback_success_time ? (
                      <span className="badge bg-light text-dark">
                        {formatDateTime(selectedDevice.last_playback_success_time)}
                      </span>
                    ) : (
                      <span className="text-muted">なし</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              閉じる
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
